/**
 * ExtAuthorize — Feb 2026 · Chrome Extension handshake page.
 *
 * Opened by the Acquilix Chrome extension when the user clicks
 * "Connect account". This page:
 *   1. Verifies the user is signed in (redirects to /login?next=... if not).
 *   2. Detects whether the extension is installed by posting an
 *      `acq-ext-ping` message and listening for the ack from the
 *      extension's content-bridge.
 *   3. On "Authorize", calls POST /api/v1/auth/issue-extension-token to
 *      mint a scoped 30-day JWT.
 *   4. Sends the token to the extension via `window.postMessage` —
 *      the content-bridge running on this origin relays it to the
 *      extension service worker (which persists it to
 *      chrome.storage.local).
 *
 * The web page NEVER talks directly to chrome.runtime — only the
 * content script (installed with the extension) can do that.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Chrome, CheckCircle2, ShieldCheck, Puzzle, ArrowRight, Loader2 } from "lucide-react";

export default function ExtAuthorize() {
  const { user, loading, activeWorkspace, activeWorkspaceId } = useAuth();
  const nav = useNavigate();
  const [extDetected, setExtDetected] = useState(null); // null | true | false
  const [extVersion, setExtVersion] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Send to login if not signed in.
  useEffect(() => {
    if (!loading && !user) {
      nav(`/login?next=${encodeURIComponent("/ext/authorize")}`, { replace: true });
    }
  }, [loading, user, nav]);

  // Ping the extension. If it's installed, its content-bridge (which
  // matches acquilix.com/*) will echo back with `acq-ext-ping-ack`.
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => { if (!cancelled) setExtDetected((v) => v === null ? false : v); }, 1500);
    function onMsg(ev) {
      if (ev.source !== window) return;
      const d = ev.data;
      if (!d || typeof d !== "object") return;
      if (d.type === "acq-ext-ping-ack" || d.type === "acq-ext-hello") {
        setExtDetected(true);
        if (d.version) setExtVersion(d.version);
      }
      if (d.type === "acq-ext-token-ack") {
        if (d.ok) {
          setDone(true);
          toast.success("Extension connected — you're all set.");
        } else {
          toast.error("Couldn't hand the token to the extension. Please retry.");
        }
        setBusy(false);
      }
    }
    window.addEventListener("message", onMsg);
    // Ping — the bridge listens for this and replies.
    window.postMessage({ type: "acq-ext-ping" }, window.location.origin);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener("message", onMsg);
    };
  }, []);

  const authorize = async () => {
    if (busy || !user) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/issue-extension-token", {
        workspace_id: activeWorkspaceId || undefined,
      });
      // Post the token to our own window — the extension's content-bridge
      // (running on acquilix.com) listens for this and forwards to its
      // service worker.
      window.postMessage({
        type: "acq-ext-token",
        payload: {
          token: data.token,
          user: data.user,
          workspace: data.workspace,
        },
      }, window.location.origin);
      // We wait for `acq-ext-token-ack` from the bridge (5s max).
      setTimeout(() => setBusy(false), 5000);
    } catch (e) {
      setBusy(false);
      toast.error(apiErrorMessage(e, "Couldn't authorise the extension"));
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="ext-authorize-page">
      <div className="max-w-2xl mx-auto py-16 px-6">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-md bg-foreground text-background flex items-center justify-center">
            <Chrome className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
              Authorise Acquilix on Chrome
            </h1>
            <p className="text-sm text-muted-foreground">
              Let the Acquilix Chrome extension add tasks on your behalf.
            </p>
          </div>
        </div>

        <div className="border border-border rounded-md p-5 mb-5" data-testid="ext-authorize-user-card">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Signed in as</div>
          <div className="font-semibold text-base">{user.name || user.email}</div>
          <div className="text-sm text-muted-foreground">{user.email}</div>
          {activeWorkspace?.name && (
            <div className="text-xs text-muted-foreground mt-2">Workspace · <strong>{activeWorkspace.name}</strong></div>
          )}
        </div>

        <div className="border border-border rounded-md p-5 mb-5" data-testid="ext-authorize-detect-card">
          <div className="flex items-center gap-3 mb-3">
            <Puzzle className="w-5 h-5 text-muted-foreground" />
            <div className="text-sm font-semibold">Extension status</div>
          </div>
          {extDetected === null && (
            <div className="text-sm text-muted-foreground flex items-center gap-2" data-testid="ext-status-checking">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking whether the Acquilix extension is installed…
            </div>
          )}
          {extDetected === true && (
            <div className="text-sm text-emerald-700 dark:text-emerald-400 flex items-center gap-2" data-testid="ext-status-installed">
              <CheckCircle2 className="w-4 h-4" />
              Extension detected {extVersion ? <span className="text-muted-foreground">(v{extVersion})</span> : null}
            </div>
          )}
          {extDetected === false && (
            <div className="text-sm" data-testid="ext-status-missing">
              <div className="text-amber-700 dark:text-amber-400 font-medium mb-1">Extension not detected</div>
              <p className="text-muted-foreground">
                Install the Acquilix extension first, then reload this page. If you're a Super Admin, download the
                latest build from{" "}
                <Link to="/super-admin/chrome-extension" className="underline">Super Admin → Chrome Extension</Link>.
              </p>
            </div>
          )}
        </div>

        <div className="border border-border rounded-md p-5 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <ShieldCheck className="w-5 h-5 text-muted-foreground" />
            <div className="text-sm font-semibold">What you're authorising</div>
          </div>
          <ul className="text-sm text-muted-foreground space-y-1.5 list-disc pl-5">
            <li>Add tasks to <strong>{activeWorkspace?.name || "your workspace"}</strong> via the extension popup.</li>
            <li>Read the list of projects in that workspace so the popup can offer a picker.</li>
            <li>Nothing else — no messaging, files, finance or HR data.</li>
            <li>The token is scoped to the extension and expires in <strong>30 days</strong>. You can revoke it any time by signing out from the extension.</li>
          </ul>
        </div>

        {done ? (
          <div className="border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 rounded-md p-5 flex items-center gap-3" data-testid="ext-authorize-done">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <div className="flex-1">
              <div className="font-semibold text-emerald-800 dark:text-emerald-300">All set.</div>
              <p className="text-sm text-emerald-700 dark:text-emerald-400">
                You can close this tab. The extension is ready — click its icon in the Chrome toolbar to add a task from anywhere.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-3">
            <Link to="/app" className="text-sm text-muted-foreground hover:text-foreground">Cancel</Link>
            <Button
              onClick={authorize}
              disabled={busy || extDetected === false}
              className="h-10 px-5"
              data-testid="ext-authorize-btn"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Authorising…</> : <>Authorise extension <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
